import {observable, action, computed, lodashMergeOverwriteArraysFn, jsondiff, _, utils} from '@api'
import {IconName} from '@blueprintjs/core'
import {ToggleFilterParams} from '@modules/catalog'
import {DashboardQueryParams, GroupByGrouping, IWorkspacePreset, nav, onSiteError, prettyPrint, site, WorkspaceConfig, WorkspacePresetDialogStore, CatalogStore, globalStores, ICatalogUserSettings} from '@stores'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {removeUndefinedsNested} from '@utils'
import {autorun, reaction, runInAction, toJS, when} from 'mobx'
import {CatalogInstanceChildStore} from './CatalogInstanceChildStore'

export const DEBUG_PRESETS = false && DEBUG

export interface ICatalogPresetES {
  creator?: string
  creationDate?: number
  copiedFromId?: string

  /**
   * JSON serialized form of IWorkspacePreset
   */
  preset?: IWorkspacePreset

  presetJSON?: string

  workspaceId?: string
  layout?: string

  label?: string
  slug?: string
  icon?: IconName
  public?: boolean
  _id?: string
}

/**
 * Mutates obj to remove any fields not existing in other
 *
 * obj = { a: { b: 'foo', c: 1}}, other: { a: { b: 'whatever'}}
 *
 * removeFieldsNotInBoth_Inline(obj, other) -> { a: { b: 'foo' }}
 * @param obj
 * @param other
 */
export function removeFieldsNotInBoth_Inline(obj: any, other: any) {
  if (_.isArray(obj) || _.isArray(other) || !_.isObject(obj) || !_.isObject(other)) return obj

  for (var k of _.keys(obj)) {
    if (!_.has(other, k)) {
      delete obj[k]
    } else {
      removeFieldsNotInBoth_Inline(obj[k], other[k])
      if (_.isEqual(obj[k], {})) {
        delete obj[k]
      }
    }
  }
}

export class CatalogPresetsStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)
    synchronizeStores(() => {
      if (catalog.isGlobalStore) {
        autorun(async () => {
          const {authStore: {user}, deployments: {deploymentConfig}} = this.stores
          if (!user || _.isEmpty(deploymentConfig)) {
            CatalogPresetsStore.presets.replace([])
          } else {
            await this.loadPresets()
          }
        }, {name: `Load user settings`})

        autorun(() => {
          const {isDirty, selectedPresetId} = this
          if (isDirty && selectedPresetId) {
            this.selectedPresetId = null
          }
        }, {name: `Detect when user has changed state that renders our selected preset invalid.`, delay: 500})
      } else {
        this.hasLoadedPresets = true
      }
    })
  }

  @computed get presetsById(): { [id: string]: ICatalogPresetES } {
    if (!this.catalog.isGlobalStore) return globalStores.catalog.presets.presetsById
    return _.keyBy(CatalogPresetsStore.presets, p => p._id)
  }

  @computed get selectedPresetId() { return this.catalog.settings.preset }

  set selectedPresetId(v) { this.catalog.settings.preset = v}

  @action clearPreset = () => {
    this.selectedPresetId = null
  }

  @computed get selectedPreset(): ICatalogPresetES {
    const {selectedPresetId, presetsById} = this
    return selectedPresetId ? presetsById[selectedPresetId] : null
  }

  get allPresets() { return CatalogPresetsStore.presets}

  /** Static as we get all presets globally across all workspaces for the deployment **/
  static presets = observable.array<ICatalogPresetES>([])
  @observable.ref dialogStore: WorkspacePresetDialogStore

  @computed get presetsByWorkspace() {
    return _.groupBy(CatalogPresetsStore.presets, p => p?.workspaceId)
  }

  @computed get presetSearchBody() {
    const {stores: {currentUser}} = this
    return currentUser?.info ? {
      size: 10000,
      query: {
        bool: {
          minimum_should_match: 1,
          should: [
            {
              term: {
                'creator.keyword': {
                  value: currentUser.info.id
                }
              }
            },
            {
              term: {
                public: {
                  value: true
                }
              }
            }
          ]
        }
      }
    } : null
  }

  @observable hasLoadedPresets = false

  @action private loadPresets = async () => {
    const {presetSearchBody, es} = this
    if (!presetSearchBody) return
    try {
      const {items} = es.unwrapHitsResult<ICatalogPresetES>(await es.search<ICatalogPresetES>({
        index: es.indexes.presets,
        body: presetSearchBody,
        meta: `Load user presets`
      }, {log: {query: false}}))

      // Fix up any presets that aren't currently JSON
      let anyFixed = false
      await Promise.all(items.map(async v => {
        if (!v.presetJSON && _.isPlainObject(v.preset)) {

          v.presetJSON = JSON.stringify(v.preset)
          return await es.genericUpsert({
            index: es.indexes.presets,
            _id: v._id,
            body: {..._.omit(v, ['_id', 'preset']), preset: null }
          })
        }
      }))

      runInAction(`Save user settings from ES`, () => {
        // console.debug(`Loaded presets from DB`, items)
        this.hasLoadedPresets = true
        CatalogPresetsStore.presets.replace(items.filter(v => v.presetJSON).map(v => ({...v, preset: JSON.parse(v.presetJSON)})))
      })
    } catch (err) {
      console.error(err)
    }
  }

  @action savePreset = async (preset: ICatalogPresetES): Promise<ICatalogPresetES> => {
    DEBUG_PRESETS && console.debug(`Saving preset - `, {preset})
    const {es, authStore: {user}} = this
    if (!user) {
      onSiteError(new Error(`You must be logged in to save a preset.`))
      return
    }

    let toSave : ICatalogPresetES = {..._.cloneDeep(_.omit(preset, 'preset')), presetJSON: JSON.stringify(preset.preset)}

    if (!toSave.creator) {
      toSave.creator = user.id
    }

    if (toSave.creator !== user.id) {
      // Save a copy? - todo - prompt the user?

      toSave = Object.assign<ICatalogPresetES, ICatalogPresetES>(toSave, {
        creator: user.id,
        creationDate: new Date().getUTCDate(),
        copiedFromId: preset._id,
        _id: undefined
      })
    }

    const {data} = await es.genericUpsert<ICatalogPresetES>({
      index: es.indexes.presets,
      _id: toSave._id,
      body: {..._.omit(toSave, '_id'), preset: null }
    })

    // console.log(data)

    const r = {_id: data['id'], ...data, preset: JSON.parse(data.presetJSON)}

    if (!preset._id) {
      CatalogPresetsStore.presets.push(r)
    }

    this.selectedPresetId = r._id
    return r
  }

  @action deletePreset = async (preset: ICatalogPresetES) => {
    const {es, stores: {currentUser}} = this
    if (!preset._id || preset.creator != currentUser.info.id) {
      onSiteError(new Error(`You do not have permission to delete preset '${preset.label}'`))
      return
    }

    const result = await es.delete(preset._id, es.indexes.presets)

    CatalogPresetsStore.presets.remove(preset)

    if (!result?.deleted) {
      //console.log({preset, result})
      onSiteError(`Unable to delete preset '${preset.label}'`)
    }
  }

  @action saveCurrentStateAsNewPreset = async () => {
    const {currentStateAsPreset: preset, workspaces} = this
    const r = await this.savePreset({preset, label: 'My Preset', workspaceId: workspaces.current})
    CatalogPresetsStore.presets.push(r)
    this.selectedPresetId = r._id
  }

  /**
   * Creates a config suitable for use in the 'customViews' section of a customer config
   */
  @computed.struct get currentStateAsPreset(): IWorkspacePreset {
    const {groupBy, catalog} = this
    if (!groupBy.initialized || !catalog.hasLoadedConfig) return null

    const {catalog: {settingsStore: {cardViewToggledOnFields}, groupBy: {isProductView, hierarchyGroups, catalogItemsGroup}, filter: {filtersWithSelections}}} = this

    const orderByToCVOrderBy = ({groupName, firstOrderBy: {field, custom, order}}: GroupByGrouping) => removeUndefinedsNested({field: groupName, sortKey: field, custom: custom ? custom : undefined, order})
    return removeUndefinedsNested(_.cloneDeep({
      catalog: {
        groupBy: groupBy.nonDefaultSettings,
        settings: _.omit(catalog.settingsStore.nonDefaultSettings, ['focusedCatalogItemId', 'freezeIds', 'preset'] as Array<keyof ICatalogUserSettings>),
        fields: _.isEmpty(cardViewToggledOnFields) ? undefined : cardViewToggledOnFields
      },
      // merchHierarchy: vlp ? _.pickBy(vlp.hierarchy.selected, v => !_.isEmpty(v)) : undefined,
      sort: {
        hierarchy: isProductView ? undefined : hierarchyGroups.map(orderByToCVOrderBy),
        catalog: catalogItemsGroup.orderBys.map(({field, order, custom}) => removeUndefinedsNested({field, order, custom: custom ? custom : undefined}))
      },
      filter: _.isEmpty(filtersWithSelections) ? undefined : {
        by: filtersWithSelections.map(({group, state: {selected, excluded}}) => ({
          key: group,
          values: Array.from(selected.keys())
        } as ToggleFilterParams))
      }
    })) as IWorkspacePreset
  }

  @computed
  private get diffWithSelectedPreset() {
    const {selectedPreset} = this

    if (selectedPreset) {
      const {currentStateAsPreset} = this, {preset} = selectedPreset
      // The view is only correct if every piece of it matches the current catalog
      let omitFields = ['id', 'label']

      //return jsondiff.diff(toJS(currentStateAsPreset, {recurseEverything: true}), toJS(selectedPreset.preset, {recurseEverything: true}))
      let r = jsondiff.diff(
        toJS(currentStateAsPreset, {recurseEverything: true}),
        toJS(preset, {recurseEverything: true})
      )

      // console.log(`Before: `, toJS({r, preset}))
      removeFieldsNotInBoth_Inline(r, preset)
      // console.log(`After: `, toJS({r, preset}))

      return r
    }

    return null
  }

  @computed get isDirty() {
    const {selectedPreset} = this

    if (selectedPreset) {
      const {diffWithSelectedPreset} = this
      // The view is only correct if every piece of it matches the current catalog
      let omitFields = ['id', 'label'],
        selectedPresetConfig = _.omit(selectedPreset, omitFields)

      return !_.isEmpty(diffWithSelectedPreset)
    }

    return false
  }

  @computed get matchingPreset(): IWorkspacePreset {
    const {catalog: {settings: {preset}}, workspaces: {workspaceConfig}} = this
    const p = CatalogPresetsStore.presets.find(p => p._id === preset)

    if (p) return p

    let r = preset ? workspaceConfig?.customViews?.find((v, i) => v._id === preset || i.toString() === preset) : null

    if (r) {
      // The view is only correct if every piece of it matches the current catalog
      let omitFields = ['id', 'label'],
        currentPresetConfig = _.cloneDeep(_.omit(toJS(this.currentStateAsPreset, {recurseEverything: true}), omitFields)),
        selectedPresetConfig = _.cloneDeep(_.omit(toJS(r, {recurseEverything: true}), omitFields))

      // console.log(`Before`, prettyPrint({currentPresetConfig, selectedPresetConfig}))
      const recurseDeleteIfNotExists = (dbConfig, liveConfig) => {
        _.keys(liveConfig).forEach(k => {
          if (!_.has(dbConfig, k)) {
            delete liveConfig[k]
          } else {
            if (_.isObject(liveConfig[k])) {
              recurseDeleteIfNotExists(dbConfig[k], liveConfig[k])
            }
          }
        })
      }
      recurseDeleteIfNotExists(selectedPresetConfig, currentPresetConfig)
      const same = _.isEqual(currentPresetConfig, selectedPresetConfig)

      if (!same) {
        // console.log({diff: diff(currentPresetConfig, selectedPresetConfig), currentPresetConfig, selectedPresetConfig})
        return null
      }

      // console.log({same, currentPresetConfig, selectedPresetConfig})
      return r
    }

    return null
  }

  @action loadPresetForCurrentWorkspace = (preset: ICatalogPresetES) => {
    if (!preset) return null

    // console.log(`loadPresetForCurrentWorkspace`, toJS(preset))
    this.loadPreset(this.workspaces.currentConfig, {...preset.preset, _id: preset._id}, true)
  }

  @action loadPreset = (workspace?: Partial<WorkspaceConfig>, p?: IWorkspacePreset, fromUserAction = true) => {
    const {isGlobalStore, catalog, workspaces, catalog: {settings, settingsStore: store, loadCustomViewFields, catalogConfig: {fields}, groupBy: {groupingByName}, groupBy, filter}, vlp} = this

    if (isGlobalStore) debugger

    if (fromUserAction) {
      DEBUG_PRESETS && console.debug(`loadPreset`, {workspace, preset: toJS(p)})
      if (workspace) {
        workspaces.current = workspace._id
      }

      // Selecting a new custom preset?
      if (p) {
        let preset = p

        // To deal with legacy presets in config - todo remove
        const oldPreset = preset as any
        if (oldPreset?.preset) {
          preset = _.cloneDeep(_.omit(preset, ['preset']))

          if (!preset.catalog) { preset.catalog = {}}

          _.merge(preset.catalog, {settings: oldPreset.preset?.catalog?.settings})
          preset.sort = _.merge({}, preset.sort, oldPreset.preset?.sort)
          preset.filter = _.merge({}, preset.filter, oldPreset.preset?.filter)
          if (oldPreset.preset?.catalog?.groupBy) {
            preset.catalog.groupBy = oldPreset.preset?.catalog?.groupBy
          }
          if (oldPreset.preset?.merchHierarchy) {
            preset.catalog.settings.vlp = _.merge({}, oldPreset.preset?.merchHierarchy)
          }
          //delete oldPreset['preset']
        }

        const {catalog: catalogConfig, filter: filterConfig, sort: sortConfig} = preset

        DEBUG_PRESETS && console.log({preset, catalogConfig, filterConfig, sortConfig})

        catalog.searcher?.clearSearch()

        if (!_.isEmpty(catalogConfig?.fields) || fields.some(f => _.includes(f.card?.customViews, preset._id))) {
          loadCustomViewFields(preset)
        }

        // If we fail to clone we will actually share the same array with the preset and groupBy.settings  YFTS-8595
        _.mergeWith(catalog.settings, _.cloneDeep(catalogConfig?.settings ?? {}), lodashMergeOverwriteArraysFn)
        _.mergeWith(catalog.groupBy.settings, _.cloneDeep(catalogConfig?.groupBy ?? {}), lodashMergeOverwriteArraysFn)

        if (catalogConfig?.settings?.vlp) {
          catalog.settings.vlp = catalogConfig?.settings?.vlp
        }

        if (catalogConfig?.settings?.cardView) {
          catalog.settings.cardView = catalogConfig?.settings?.cardView
        }

        catalog.settings.preset = preset._id

        // Replaced with real presets and vlp in catalog config
        // if (catalog?.settings?.vlp?.selected) {
        //   const {vlp: {selected: {plan, plans, collection, collections, folder, folders} = catalog.settings,
        //     {hierarchy: {selected, planByName, folderByName, collectionByName, collectionContainedBy}} = vlp
        //
        //   if (plan || !_.isEmpty(plans)) {
        //     selected.plans = plan ? utils.enlist<string>(plan).map(p => planByName[p]?.id).filter(_.identity) : plans
        //
        //     selected.folders = folders ? folders :_.isEmpty(folder) ? [] : [utils.enlist(folder).map(name => folderByName[name]?.find(f => _.includes(selected.plans, f.plan.id))?.id).filter(_.identity)].filter(arr => !_.isEmpty(arr))
        //
        //     // Collections are specified in the preset by name but there will be multiple C12345 ids in centric with the same name but different hierarchies.  Find the one that matches our plans/folders
        //     selected.collections = collections ?? (_.isEmpty(collection) ? [] : utils.enlist(collection).map(
        //       name => {
        //         const matchingCollections = collectionByName[name]
        //
        //         if (!matchingCollections) {
        //           dialogs.showMessage(`A collection named '${name}' could not be found.`)
        //         }
        //         return matchingCollections?.find(c => selected.folders.some(h => collectionContainedBy(c.id, _.last(h))))?.id
        //       }).filter(_.identity))
        //   }
        // }

        /// region Sorting Custom View Config
        sortConfig?.hierarchy?.forEach(({field, sortKey, custom, order}) => {
          const g = groupingByName.get(field)
          if (!g) {
            console.warn(`Custom view '${preset._id}' has groupBy sort field '${field}' defined but that field is not part of the hierarchy - ignoring`)
          } else {
            g.orderBy.replace([{field: sortKey ?? '_key', order, custom}])
          }
        })

        if (sortConfig?.catalog) {
          when(() => groupBy?.catalogItemsGroup?.orderBy != null, () => {
            groupBy.catalogItemsGroup.orderBy.replace(sortConfig.catalog)
          })
        }

        /// endregion

        if (filterConfig) {
          filter.resetFilters(false)

          filterConfig?.by?.forEach((f: ToggleFilterParams) => filter.toggleFilterBatched({...f, forceSet: true}))
        }

        this.selectedPresetId = preset._id
      } else {
        settings.cardView = _.cloneDeep(catalog.cv.defaultCardView)
        settings.groupBy.hierarchy = []
        settings.preset = null
        settings.vlp.selected = {}
      }
    }
  }

  @observable dialogPreset: ICatalogPresetES = null

  @action saveCurrentPreset = () => {
    const {selectedPreset} = this

    selectedPreset && this.savePreset(selectedPreset)
  }

  startCreateNewPreset = (preset?: ICatalogPresetES) => {
    this.dialogPreset = _.cloneDeep(preset ?? {preset: this.currentStateAsPreset, label: 'My Preset', workspaceId: this.workspaces.current})
  }

  @action onCloseDialog = () => {

  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogPresetsStore.ts